import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  List,
  ListItem,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation } from 'react-i18next';
import { searchVocabulary } from '../../services/mockMappingService';

// Eingabeverzoegerung, damit nicht bei jedem Tastendruck gesucht wird (#103).
const SEARCH_DEBOUNCE_MS = 300;

const OMOP_VOCABULARY_SEARCH_COPY = {
  de: {
    title: 'OMOP Vocabulary Suche',
    intro: 'Durchsuchen Sie die lokale OMOP Vocabulary Datenbank, um manuell ein passendes Konzept auszuwählen.',
    searchTerm: 'Suchbegriff',
    searchPlaceholder: 'z.B. Systolic blood pressure',
    vocabularyLabel: 'Vocabulary',
    domainLabel: 'Domain',
    all: 'Alle',
    searchLoading: 'Suche läuft...',
    search: 'Suchen',
    results: 'Suchergebnisse',
    standard: 'Standard',
    nonStandard: 'Nicht-Standard',
    synonyms: 'Synonyme',
    useSelected: 'Ausgewähltes Konzept verwenden',
    help: 'Geben Sie einen Suchbegriff ein, um OMOP Konzepte zu finden.',
    noResults: 'Keine Ergebnisse für "{{query}}"',
    searchError: 'Die Suche ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
  },
  en: {
    title: 'OMOP vocabulary search',
    intro: 'Search the local OMOP vocabulary database to manually select a matching concept.',
    searchTerm: 'Search term',
    searchPlaceholder: 'e.g. Systolic blood pressure',
    vocabularyLabel: 'Vocabulary',
    domainLabel: 'Domain',
    all: 'All',
    searchLoading: 'Searching...',
    search: 'Search',
    results: 'Search results',
    standard: 'Standard',
    nonStandard: 'Non-standard',
    synonyms: 'Synonyms',
    useSelected: 'Use selected concept',
    help: 'Enter a search term to find OMOP concepts.',
    noResults: 'No results for "{{query}}"',
    searchError: 'The search failed. Please try again.',
  },
  pt: {
    title: 'Pesquisa no vocabulário OMOP',
    intro: 'Pesquise na base de dados local do vocabulário OMOP para selecionar manualmente um conceito adequado.',
    searchTerm: 'Termo de pesquisa',
    searchPlaceholder: 'ex.: Systolic blood pressure',
    vocabularyLabel: 'Vocabulário',
    domainLabel: 'Domínio',
    all: 'Todos',
    searchLoading: 'A pesquisar...',
    search: 'Pesquisar',
    results: 'Resultados da pesquisa',
    standard: 'Padrão',
    nonStandard: 'Não padrão',
    synonyms: 'Sinónimos',
    useSelected: 'Usar conceito selecionado',
    help: 'Introduza um termo de pesquisa para encontrar conceitos OMOP.',
    noResults: 'Nenhum resultado para "{{query}}"',
    searchError: 'A pesquisa falhou. Por favor tente novamente.',
  },
};

/**
 * OmopVocabularySearch Component
 * Manual OMOP vocabulary search interface
 * Allows users to search local vocabulary database and select concepts manually
 *
 * @param {object} props
 * @param {function} props.onSelectConcept - Callback when user selects a concept
 * @param {string} props.parameterName - Current parameter name for context
 * @param {boolean} props.isSearching - Whether search is in progress
 */
const OmopVocabularySearch = ({
  onSelectConcept,
  parameterName,
  isSearching = false
}) => {
  const { i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [vocabularyFilter, setVocabularyFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const debounceTimerRef = useRef(null);
  const searchGenerationRef = useRef(0);
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = OMOP_VOCABULARY_SEARCH_COPY[language];

  const runSearch = useCallback(async (query, filters, page) => {
    // Generationszaehler: eine veraltete Antwort (z.B. bei geleertem Suchfeld
    // waehrend eine langsamere Anfrage noch laeuft) darf den sichtbaren
    // Zustand einer inzwischen ausgeloesten neueren Anfrage nicht ueberschreiben.
    const generation = ++searchGenerationRef.current;
    const isCurrent = () => generation === searchGenerationRef.current;

    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      if (isCurrent()) {
        setSearchResults([]);
        setTotalPages(1);
        setSearchError(false);
        setIsFetching(false);
        setSelectedConcept(null);
      }
      return;
    }

    setIsFetching(true);
    setSearchError(false);
    try {
      const response = await searchVocabulary(trimmedQuery, filters, page);
      if (isCurrent()) {
        const results = response.results || [];
        setSearchResults(results);
        setTotalPages(response.pagination?.totalPages || 1);
        // Eine Auswahl aus der vorigen Trefferliste ueberlebt nur, wenn sie
        // auch in der neuen Liste vorkommt -- sonst zeigt der "Uebernehmen"-
        // Button ein Konzept an, das nirgends mehr sichtbar ist (B4).
        setSelectedConcept((prev) => (
          prev && results.some((c) => c.conceptId === prev.conceptId) ? prev : null
        ));
      }
    } catch {
      if (isCurrent()) {
        setSearchResults([]);
        setTotalPages(1);
        setSearchError(true);
        setSelectedConcept(null);
      }
    } finally {
      if (isCurrent()) {
        setIsFetching(false);
      }
    }
  }, []);

  const currentFilters = { vocabulary: vocabularyFilter, domain: domainFilter };

  // Eingabeverzoegerung: eine Suche je Pause im Tippen/Filtern, nicht je Tastendruck.
  useEffect(() => {
    setCurrentPage(1);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      runSearch(searchQuery, currentFilters, 1);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(debounceTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, vocabularyFilter, domainFilter, runSearch]);

  const handleSearch = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setCurrentPage(1);
    runSearch(searchQuery, currentFilters, 1);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    runSearch(searchQuery, currentFilters, page);
  };

  const handleSelectConcept = (concept) => {
    setSelectedConcept(concept);
  };

  const handleConfirmSelection = () => {
    if (selectedConcept && onSelectConcept) {
      onSelectConcept(selectedConcept);
    }
  };

  useEffect(() => {
    // Initialize with parameter name as default search query
    if (parameterName) {
      setSearchQuery(parameterName);
    }
  }, [parameterName]);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {copy.title}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {copy.intro}
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Search Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={copy.searchTerm}
            placeholder={copy.searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>{copy.vocabularyLabel}</InputLabel>
            <Select
              value={vocabularyFilter}
              label={copy.vocabularyLabel}
              onChange={(e) => setVocabularyFilter(e.target.value)}
            >
              <MenuItem value="all">{copy.all}</MenuItem>
              <MenuItem value="LOINC">LOINC</MenuItem>
              <MenuItem value="SNOMED">SNOMED</MenuItem>
              <MenuItem value="RxNorm">RxNorm</MenuItem>
              <MenuItem value="ICD10">ICD10</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>{copy.domainLabel}</InputLabel>
            <Select
              value={domainFilter}
              label={copy.domainLabel}
              onChange={(e) => setDomainFilter(e.target.value)}
            >
              <MenuItem value="all">{copy.all}</MenuItem>
              <MenuItem value="Measurement">Measurement</MenuItem>
              <MenuItem value="Observation">Observation</MenuItem>
              <MenuItem value="Condition">Condition</MenuItem>
              <MenuItem value="Procedure">Procedure</MenuItem>
              <MenuItem value="Drug">Drug</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Button
        variant="contained"
        startIcon={(isSearching || isFetching) ? <CircularProgress size={20} /> : <SearchIcon />}
        onClick={handleSearch}
        disabled={!searchQuery.trim() || isSearching || isFetching}
        fullWidth
        sx={{ mb: 3 }}
      >
        {(isSearching || isFetching) ? copy.searchLoading : copy.search}
      </Button>

      {/* Search Results */}
      {searchResults.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {copy.results} ({searchResults.length})
          </Typography>
          <List sx={{ mb: 2 }}>
            {searchResults.map((concept, idx) => (
              <ListItem key={idx} sx={{ p: 0, mb: 1 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    width: '100%',
                    cursor: 'pointer',
                    border: selectedConcept?.conceptId === concept.conceptId
                      ? '2px solid #1976d2'
                      : '1px solid #e0e0e0',
                    backgroundColor: selectedConcept?.conceptId === concept.conceptId
                      ? '#e3f2fd'
                      : 'white',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                  onClick={() => handleSelectConcept(concept)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight="bold">
                        {concept.conceptName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {concept.conceptId} • Code: {concept.conceptCode}
                      </Typography>
                    </Box>
                    {selectedConcept?.conceptId === concept.conceptId && (
                      <CheckIcon color="primary" />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Chip label={concept.vocabulary} size="small" variant="outlined" />
                    <Chip label={concept.domain} size="small" variant="outlined" />
                    {concept.standardConcept === 'S' ? (
                      <Chip label={copy.standard} size="small" color="success" />
                    ) : (
                      <Chip label={copy.nonStandard} size="small" variant="outlined" />
                    )}
                  </Box>
                  {concept.conceptSynonyms && concept.conceptSynonyms.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {copy.synonyms}: {concept.conceptSynonyms.slice(0, 3).join(', ')}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      startIcon={<LinkIcon />}
                      href={`https://athena.ohdsi.org/search-terms/terms/${concept.conceptId}`}
                      target="_blank"
                      onClick={(e) => e.stopPropagation()}
                    >
                      ATHENA
                    </Button>
                  </Box>
                </Paper>
              </ListItem>
            ))}
          </List>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}

          {/* Confirm Selection */}
          {selectedConcept && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckIcon />}
              onClick={handleConfirmSelection}
              fullWidth
            >
              {copy.useSelected}
            </Button>
          )}
        </Box>
      ) : isFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : searchError ? (
        <Alert severity="error">{copy.searchError}</Alert>
      ) : searchQuery.trim() ? (
        <Alert severity="info">{copy.noResults.replace('{{query}}', searchQuery)}</Alert>
      ) : (
        <Alert severity="info">
          <Typography variant="body2">
            <InfoIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            {copy.help}
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default OmopVocabularySearch;
